import React, { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import {
  requestOtpRequest,
  verifyOtpRequest,
  updatePasswordRequest,
  authResetState,
} from "../../redux/auth/AuthAction";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import CustomButton from "../constant/commonButton";
import { Avatar } from "@mui/material";
import { StyledBox, StyledContainer, styles } from "../components/LoginStyles";
import "../../style/register.css";
import Logo from "../../image/boozLogo.png";
import FormInput from "../constant/commonForm";
import CommonDialog from "../notification/ErrorDialogbox";
import { mapBackendMessage } from "../notification/BackendMessage";
import { APP_LINK } from "../global/MenuItems";

const OTP_EXPIRY_TIME = 45;

const Forgot = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const timerRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(45);
  const [isOtpExpired, setIsOtpExpired] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [step, setStep] = useState(1);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  const form = useForm();
  const { register, handleSubmit, formState, watch, control, reset, setError } =
    form;
  const { errors } = formState;
  const forgotState = useSelector((state) => state?.auth || {});

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handlelogin = () => {
    dispatch(authResetState());
    navigate(APP_LINK.LOGIN);
  };

  const email = watch("email");
  const password = watch("password");

  // Show password requirements when user starts typing password
  useEffect(() => {
    if (step === 3 && password && password.length > 0) {
      setShowPasswordRequirements(true);
    } else {
      setShowPasswordRequirements(false);
    }
  }, [password, step]);

  const startOtpTimer = () => {
    setTimeLeft(OTP_EXPIRY_TIME);
    setIsOtpExpired(false);
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          if (!otpVerified) {
            setIsOtpExpired(true);
            setOtpSent(false);
            setStep(1);
            setDialogMessage("OTP has expired. Please request a new OTP.");
            setIsSuccess(false);
            setDialogOpen(true);
            dispatch(authResetState());
            reset({ otp: "" });
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const onSendOtp = (data) => {
    if (data?.email) {
      const otpRequestData = {
        email: data?.email,
        subject_type: "2",
      };
      dispatch(requestOtpRequest(otpRequestData));
    }
  };

  const handleOtpResponse = () => {
    if (forgotState?.requestOtp?.error) {
      const errorMessage = forgotState?.requestOtp?.errormessage;
      const errorFields = forgotState?.requestOtp?.errorFields || {};

      if (Object.keys(errorFields).length > 0) {
        Object.keys(errorFields).forEach((field) => {
          setError(field, {
            type: "manual",
            message: errorFields[field],
          });
        });
      } else if (typeof errorMessage === "string") {
        setError("email", {
          type: "manual",
          message: errorMessage,
        });
      } else {
        const customMessage = mapBackendMessage(
          forgotState?.requestOtp?.errormessage ||
            "An error occurred while sending OTP."
        );
        setDialogMessage(customMessage);
        setIsSuccess(false);
        setDialogOpen(true);
      }
      dispatch(authResetState());
    } else if (forgotState?.requestOtp?.success) {
      setDialogMessage("OTP sent to your email successfully");
      setIsSuccess(true);
      setDialogOpen(true);
      setOtpSent(true);
      setStep(2);
      startOtpTimer();
      setTimeout(() => {
        setDialogOpen(false);
      }, 1000);
      dispatch(authResetState());
    }
  };

  const onVerifyOtp = (data) => {
    if (isOtpExpired) {
      setError("otp", {
        type: "manual",
        message: "OTP has expired. Please request a new OTP.",
      });
      return;
    }

    if (data?.otp && email) {
      const otpVerifyData = {
        email: email,
        verification_code: data.otp,
      };
      dispatch(verifyOtpRequest(otpVerifyData));
    } else {
      setError("otp", {
        type: "manual",
        message: "OTP is required to proceed",
      });
    }
  };

  // Handle OTP verification response
  const handleVerifyOtpResponse = () => {
    if (forgotState?.registerOtp?.error) {
      const errorMessage = forgotState?.registerOtp?.errormessage;
      const errorFields = forgotState?.registerOtp?.errorFields || {};
      
      if (Object.keys(errorFields).length > 0) {
        Object.keys(errorFields).forEach((field) => {
          setError(field, {
            type: "manual",
            message: errorFields[field],
          });
        });
      } else if (typeof errorMessage === "string") {
        setError("otp", {
          type: "manual",
          message: errorMessage,
        });
      } else {
        const customMessage = mapBackendMessage(
          forgotState?.registerOtp?.errormessage ||
            "An error occurred while verifying OTP."
        );
        setDialogMessage(customMessage);
        setIsSuccess(false);
        setDialogOpen(true);
      }
      dispatch(authResetState());
    } else if (forgotState?.registerOtp?.success) {
      setDialogMessage("OTP verified successfully");
      setIsSuccess(true);
      setDialogOpen(true);
      setOtpVerified(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setStep(3);
      setTimeout(() => {
        setDialogOpen(false);
      }, 1000);
      dispatch(authResetState());
    }
  };

  const onUpdatePassword = (data) => {
    dispatch(
      updatePasswordRequest({
        email: email, // Use email from watch instead of data.email
        password: data?.password,
        confirmPassword: data?.confirmPassword,
      })
    );
  };

  const handleUpdatePasswordResponse = () => {
    if (forgotState?.forgotPassword?.error) {
      const customMessage = mapBackendMessage(
        forgotState?.forgotPassword?.errormessage ||
          "An error occurred while updating password."
      );
      setDialogMessage(customMessage);
      setIsSuccess(false);
      setDialogOpen(true);
      dispatch(authResetState());
    } else if (forgotState?.forgotPassword?.success) {
      setDialogMessage("Password updated successfully");
      setIsSuccess(true);
      setDialogOpen(true);
      setPasswordUpdated(true);
      setTimeout(() => {
        navigate(APP_LINK.LOGIN);
      }, 2000); // Increased delay to ensure user sees the success message
      dispatch(authResetState());
    }
  };

  const onSubmit = (data) => {
    if (step === 1) {
      onSendOtp(data);
    } else if (step === 2) {
      onVerifyOtp(data);
    } else if (step === 3) {
      onUpdatePassword(data);
    }
  };

  // Single useEffect to handle all responses based on current step
  useEffect(() => {
    if (step === 1 && forgotState?.requestOtp) {
      handleOtpResponse();
    } else if (step === 2 && forgotState?.registerOtp) {
      handleVerifyOtpResponse();
    } else if (step === 3 && forgotState?.forgotPassword) {
      handleUpdatePasswordResponse();
    }
  }, [
    forgotState?.requestOtp,
    forgotState?.registerOtp,
    forgotState?.forgotPassword,
    step,
  ]);

  return (
    <StyledBox>
      <StyledContainer maxWidth="xs">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="login-page">
            <Grid item xs={12} align="center">
              <Avatar src={Logo} className="avatar" />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                align="center"
                fontWeight={600}
                gutterBottom
              >
                Forgot Password
              </Typography>
            </Grid>

            {/* Email Field */}
            {step === 1 && (
              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: step === 1 && "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: "Invalid email format",
                    },
                  }}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Email Address"
                      type="email"
                      fullWidth
                      inputProps={{
                        className: "text-center",
                        ...register("email"),
                      }}
                      error={!!errors?.email}
                      helperText={errors?.email?.message}
                    />
                  )}
                />
              </Grid>
            )}

            {/* OTP Field */}
            {step === 2 && (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  align="center"
                  sx={{ marginBottom: "5px", color: timeLeft <= 15 ? "red" : "#ff3300" }}
                >
                  Time left: {timeLeft} seconds
                </Typography>
                <Controller
                  name="otp"
                  control={control}
                  rules={{
                    required: step === 2 && "OTP is required",
                    pattern: {
                      value: /^[0-9]{6}$/,
                      message: "Invalid OTP format (6 digits required)",
                    },
                  }}
                  render={({ field }) => (
                    <FormInput
                      {...field}
                      label="Enter 6-digit OTP"
                      type="text"
                      fullWidth
                      inputProps={{
                        className: "text-center",
                        maxLength: 6,
                        ...register("otp"),
                      }}
                      error={!!errors?.otp}
                      helperText={errors?.otp?.message}
                    />
                  )}
                />
              </Grid>
            )}

            {/* Password Fields */}
            {step === 3 && (
              <>
                <Grid item xs={12}>
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: step === 3 && "Password is required",
                      pattern: {
                        value:
                          /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,12}$/,
                        message: "Password must meet the requirements below",
                      },
                    }}
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        label="New Password"
                        type="password"
                        fullWidth
                        inputProps={{
                          className: "text-center",
                          ...register("password"),
                        }}
                        error={!!errors?.password}
                        helperText={errors?.password?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{
                      required: step === 3 && "Confirm Password is required",
                      validate: (value) =>
                        value === watch("password") || "Passwords do not match",
                    }}
                    render={({ field }) => (
                      <FormInput
                        {...field}
                        label="Confirm The New Password"
                        type="password"
                        fullWidth
                        inputProps={{
                          className: "text-center",
                          ...register("confirmPassword"),
                        }}
                        error={!!errors?.confirmPassword}
                        helperText={errors?.confirmPassword?.message}
                      />
                    )}
                  />
                  {/* Password Requirements */}
                  {showPasswordRequirements && (
                    <Box sx={styles.signupShowPassword}>
                      <Typography
                        variant="body2"
                        sx={styles.signupShowPasswordTypo}
                      >
                        Password Requirements:
                      </Typography>
                      <Typography
                        variant="body2"
                        component="ul"
                        sx={styles.signupShowPasswordBody}
                      >
                        <li>Minimum 8 characters</li>
                        <li>At least 1 uppercase letter (A-Z)</li>
                        <li>At least 1 number (0-9)</li>
                        <li>At least 1 special character (!@#$%^&*)</li>
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </>
            )}

            {/* Submit Button */}
            <Grid item xs={12}>
              <CustomButton
                text={
                  step === 1 ? "Send OTP" : step === 2 ? "Verify OTP" : "Update Password"
                }
                variant="contained"
                size="large"
                type="submit"
                buttonStyle="StyledButton"
                disabled={isOtpExpired && step === 2}
                loading={
                  forgotState?.requestOtp?.inProgress ||
                  forgotState?.registerOtp?.inProgress ||
                  forgotState?.forgotPassword?.inProgress
                }
              />
            </Grid>
            <Grid item xs={12} align="center">
              <Typography variant="body2" align="center" gutterBottom>
                - - - - - - - - - - or - - - - - - - - -
              </Typography>
              <Link to="/" style={styles.loginDirectly}>
                <Typography
                  variant="body2"
                  align="center"
                  padding="8px"
                  gutterBottom
                  onClick={handlelogin}
                >
                  Back To Login ?
                </Typography>
              </Link>
            </Grid>
          </Grid>
        </form>
      </StyledContainer>

      {/* Error Dialog */}
      <CommonDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        isSuccess={isSuccess}
        messageSuccess={dialogMessage}
        messageError={dialogMessage}
      />
    </StyledBox>
  );
};

export default Forgot;